/**
 * PremiumCharts.jsx
 * Sistema de gráficos premium usando Recharts
 *
 * Features:
 * - Dark/Light mode automático via tokens
 * - Tooltip customizado com glassmorphism
 * - Legenda estilizada
 * - Grid sutil
 * - Animações suaves
 * - Responsivo
 * - Loading/Empty states
 *
 * Componentes:
 * - PremiumLineChart
 * - PremiumAreaChart
 * - PremiumBarChart
 * - PremiumPieChart
 * - PremiumDonutChart
 *
 * Uso:
 *   import { PremiumLineChart, PremiumBarChart } from './premium';
 *
 *   <PremiumLineChart
 *     data={speedData}
 *     xKey="time"
 *     lines={[{ dataKey: 'speed', name: 'Velocidade' }]}
 *   />
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Skeleton } from '@mui/material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { usePremiumTokens } from '../../theme/usePremiumTokens';
import { useTranslation } from '../LocalizationProvider';

// ============================================================================
// CONSTANTES
// ============================================================================

const DEFAULT_HEIGHT = 300;
const ANIMATION_DURATION = 800;

// ============================================================================
// HOOK: useChartColors
// ============================================================================

/**
 * Hook para obter cores da paleta de gráficos
 */
export const useChartColors = () => {
  const tokens = usePremiumTokens();
  return {
    series: tokens.chart?.series || [
      '#00d4aa', '#58a6ff', '#a371f7', '#d29922', '#f97316',
      '#f85149', '#3fb950', '#db61a2', '#79c0ff', '#7ee787',
    ],
    positive: tokens.chart?.positive || '#00d4aa',
    negative: tokens.chart?.negative || '#f85149',
    neutral: tokens.chart?.neutral || '#8b949e',
    grid: tokens.chart?.grid || 'rgba(255, 255, 255, 0.06)',
    axis: tokens.chart?.axis || '#6e7681',
    axisLabel: tokens.chart?.axisLabel || '#8b949e',
  };
};

// ============================================================================
// COMPONENTE: ChartTooltip
// ============================================================================

export const ChartTooltip = ({ active, payload, label, formatter, labelFormatter }) => {
  const tokens = usePremiumTokens();

  if (!active || !payload || payload.length === 0) return null;

  const displayLabel = labelFormatter ? labelFormatter(label) : label;

  return (
    <Box
      sx={{
        background: tokens.chart?.tooltip?.bg || 'rgba(22, 27, 34, 0.95)',
        backdropFilter: 'blur(8px)',
        border: `1px solid ${tokens.chart?.tooltip?.border || 'rgba(255,255,255,0.1)'}`,
        borderRadius: tokens.radius?.md || '6px',
        padding: tokens.spacing?.[2] || '8px',
        boxShadow: tokens.shadows?.lg || '0 10px 15px -3px rgba(0,0,0,0.3)',
        minWidth: 120,
      }}
    >
      {displayLabel && (
        <Typography
          sx={{
            color: tokens.chart?.tooltip?.text || '#f0f6fc',
            fontSize: tokens.typography?.fontSize?.sm || '0.875rem',
            fontWeight: 600,
            mb: 0.5,
            background: 'transparent',
          }}
        >
          {displayLabel}
        </Typography>
      )}
      {payload.map((entry, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            mt: 0.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: entry.color,
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                color: tokens.chart?.tooltip?.textSecondary || '#8b949e',
                fontSize: tokens.typography?.fontSize?.xs || '0.75rem',
                background: 'transparent',
              }}
            >
              {entry.name}
            </Typography>
          </Box>
          <Typography
            sx={{
              color: tokens.chart?.tooltip?.text || '#f0f6fc',
              fontSize: tokens.typography?.fontSize?.xs || '0.75rem',
              fontWeight: 600,
              background: 'transparent',
            }}
          >
            {formatter ? formatter(entry.value, entry.name) : entry.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

ChartTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  label: PropTypes.any,
  formatter: PropTypes.func,
  labelFormatter: PropTypes.func,
};

// ============================================================================
// COMPONENTE: ChartLegend
// ============================================================================

export const ChartLegend = ({ payload }) => {
  const tokens = usePremiumTokens();

  if (!payload || payload.length === 0) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: 2,
        mt: 2,
      }}
    >
      {payload.map((entry, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
          }}
        >
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: entry.type === 'line' ? '2px' : '50%',
              bgcolor: entry.color,
            }}
          />
          <Typography
            sx={{
              color: tokens.text?.secondary || '#8b949e',
              fontSize: tokens.typography?.fontSize?.xs || '0.75rem',
              background: 'transparent',
            }}
          >
            {entry.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

ChartLegend.propTypes = {
  payload: PropTypes.array,
};

// ============================================================================
// COMPONENTE: ChartSkeleton (Loading State)
// ============================================================================

const ChartSkeleton = ({ height = DEFAULT_HEIGHT }) => {
  const tokens = usePremiumTokens();

  return (
    <Box
      sx={{
        height,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        p: 2,
      }}
    >
      <Skeleton
        variant="text"
        width="30%"
        sx={{ bgcolor: tokens.surface?.hover || 'rgba(255,255,255,0.05)' }}
      />
      <Skeleton
        variant="rectangular"
        height={height - 60}
        sx={{
          bgcolor: tokens.surface?.hover || 'rgba(255,255,255,0.05)',
          borderRadius: tokens.radius?.md || '6px',
        }}
      />
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
        <Skeleton variant="text" width={60} sx={{ bgcolor: tokens.surface?.hover || 'rgba(255,255,255,0.05)' }} />
        <Skeleton variant="text" width={60} sx={{ bgcolor: tokens.surface?.hover || 'rgba(255,255,255,0.05)' }} />
        <Skeleton variant="text" width={60} sx={{ bgcolor: tokens.surface?.hover || 'rgba(255,255,255,0.05)' }} />
      </Box>
    </Box>
  );
};

// ============================================================================
// COMPONENTE: EmptyChart (Empty State)
// ============================================================================

const EmptyChart = ({ height = DEFAULT_HEIGHT, message }) => {
  const tokens = usePremiumTokens();
  const t = useTranslation();

  return (
    <Box
      sx={{
        height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 1,
        backgroundColor: tokens.surface?.sunken || 'rgba(0,0,0,0.2)',
        borderRadius: tokens.radius?.lg || '8px',
        border: `1px dashed ${tokens.border?.subtle || 'rgba(255,255,255,0.1)'}`,
      }}
    >
      <Typography
        sx={{
          color: tokens.text?.muted || '#6e7681',
          fontSize: tokens.typography?.fontSize?.sm || '0.875rem',
        }}
      >
        {message || t('chartNoData') || 'Nenhum dado disponível'}
      </Typography>
    </Box>
  );
};

// ============================================================================
// COMPONENTE: PremiumLineChart
// ============================================================================

export const PremiumLineChart = ({
  data,
  xKey,
  lines,
  height = DEFAULT_HEIGHT,
  loading = false,
  emptyMessage,
  showGrid = true,
  showLegend = true,
  showTooltip = true,
  animate = true,
  xAxisFormatter,
  yAxisFormatter,
  yAxisUnit = '',
  tooltipFormatter,
  tooltipLabelFormatter,
}) => {
  const tokens = usePremiumTokens();
  const chartColors = useChartColors();

  if (loading) return <ChartSkeleton height={height} />;
  if (!data || data.length === 0) return <EmptyChart height={height} message={emptyMessage} />;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
        {showGrid && (
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={chartColors.grid}
            vertical={false}
          />
        )}
        <XAxis
          dataKey={xKey}
          stroke={chartColors.axis}
          tick={{ fill: chartColors.axisLabel, fontSize: 11 }}
          tickLine={{ stroke: chartColors.axis }}
          axisLine={{ stroke: chartColors.axis }}
          tickFormatter={xAxisFormatter}
        />
        <YAxis
          stroke={chartColors.axis}
          tick={{ fill: chartColors.axisLabel, fontSize: 11 }}
          tickLine={{ stroke: chartColors.axis }}
          axisLine={{ stroke: chartColors.axis }}
          tickFormatter={yAxisFormatter}
          unit={yAxisUnit}
        />
        {showTooltip && (
          <Tooltip
            content={
              <ChartTooltip
                formatter={tooltipFormatter}
                labelFormatter={tooltipLabelFormatter}
              />
            }
          />
        )}
        {showLegend && <Legend content={<ChartLegend />} />}
        {lines.map((line, index) => (
          <Line
            key={line.dataKey}
            type="monotone"
            dataKey={line.dataKey}
            name={line.name || line.dataKey}
            stroke={line.color || chartColors.series[index % chartColors.series.length]}
            strokeWidth={line.strokeWidth || 2}
            strokeDasharray={line.dashed ? '5 5' : undefined}
            dot={line.showDots !== false ? { r: 3, fill: line.color || chartColors.series[index % chartColors.series.length] } : false}
            activeDot={{ r: 5 }}
            isAnimationActive={animate}
            animationDuration={ANIMATION_DURATION}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
};

PremiumLineChart.propTypes = {
  data: PropTypes.array.isRequired,
  xKey: PropTypes.string.isRequired,
  lines: PropTypes.arrayOf(
    PropTypes.shape({
      dataKey: PropTypes.string.isRequired,
      name: PropTypes.string,
      color: PropTypes.string,
      strokeWidth: PropTypes.number,
      dashed: PropTypes.bool,
      showDots: PropTypes.bool,
    })
  ).isRequired,
  height: PropTypes.number,
  loading: PropTypes.bool,
  emptyMessage: PropTypes.string,
  showGrid: PropTypes.bool,
  showLegend: PropTypes.bool,
  showTooltip: PropTypes.bool,
  animate: PropTypes.bool,
  xAxisFormatter: PropTypes.func,
  yAxisFormatter: PropTypes.func,
  yAxisUnit: PropTypes.string,
  tooltipFormatter: PropTypes.func,
  tooltipLabelFormatter: PropTypes.func,
};

// ============================================================================
// COMPONENTE: PremiumAreaChart
// ============================================================================

export const PremiumAreaChart = ({
  data,
  xKey,
  areas,
  height = DEFAULT_HEIGHT,
  loading = false,
  emptyMessage,
  showGrid = true,
  showLegend = true,
  showTooltip = true,
  animate = true,
  xAxisFormatter,
  yAxisFormatter,
  yAxisUnit = '',
  tooltipFormatter,
  tooltipLabelFormatter,
  stacked = false,
}) => {
  const tokens = usePremiumTokens();
  const chartColors = useChartColors();

  if (loading) return <ChartSkeleton height={height} />;
  if (!data || data.length === 0) return <EmptyChart height={height} message={emptyMessage} />;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
        <defs>
          {areas.map((area, index) => {
            const color = area.color || chartColors.series[index % chartColors.series.length];
            return (
              <linearGradient key={area.dataKey} id={`gradient-${area.dataKey}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={color} stopOpacity={area.fillOpacity || 0.3} />
                <stop offset="95%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            );
          })}
        </defs>
        {showGrid && (
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={chartColors.grid}
            vertical={false}
          />
        )}
        <XAxis
          dataKey={xKey}
          stroke={chartColors.axis}
          tick={{ fill: chartColors.axisLabel, fontSize: 11 }}
          tickLine={{ stroke: chartColors.axis }}
          axisLine={{ stroke: chartColors.axis }}
          tickFormatter={xAxisFormatter}
        />
        <YAxis
          stroke={chartColors.axis}
          tick={{ fill: chartColors.axisLabel, fontSize: 11 }}
          tickLine={{ stroke: chartColors.axis }}
          axisLine={{ stroke: chartColors.axis }}
          tickFormatter={yAxisFormatter}
          unit={yAxisUnit}
        />
        {showTooltip && (
          <Tooltip
            content={
              <ChartTooltip
                formatter={tooltipFormatter}
                labelFormatter={tooltipLabelFormatter}
              />
            }
          />
        )}
        {showLegend && <Legend content={<ChartLegend />} />}
        {areas.map((area, index) => {
          const color = area.color || chartColors.series[index % chartColors.series.length];
          return (
            <Area
              key={area.dataKey}
              type="monotone"
              dataKey={area.dataKey}
              name={area.name || area.dataKey}
              stroke={color}
              strokeWidth={2}
              fill={`url(#gradient-${area.dataKey})`}
              stackId={stacked ? 'stack' : undefined}
              isAnimationActive={animate}
              animationDuration={ANIMATION_DURATION}
            />
          );
        })}
      </AreaChart>
    </ResponsiveContainer>
  );
};

PremiumAreaChart.propTypes = {
  data: PropTypes.array.isRequired,
  xKey: PropTypes.string.isRequired,
  areas: PropTypes.arrayOf(
    PropTypes.shape({
      dataKey: PropTypes.string.isRequired,
      name: PropTypes.string,
      color: PropTypes.string,
      fillOpacity: PropTypes.number,
    })
  ).isRequired,
  height: PropTypes.number,
  loading: PropTypes.bool,
  emptyMessage: PropTypes.string,
  showGrid: PropTypes.bool,
  showLegend: PropTypes.bool,
  showTooltip: PropTypes.bool,
  animate: PropTypes.bool,
  xAxisFormatter: PropTypes.func,
  yAxisFormatter: PropTypes.func,
  yAxisUnit: PropTypes.string,
  tooltipFormatter: PropTypes.func,
  tooltipLabelFormatter: PropTypes.func,
  stacked: PropTypes.bool,
};

// ============================================================================
// COMPONENTE: PremiumBarChart
// ============================================================================

export const PremiumBarChart = ({
  data,
  xKey,
  bars,
  height = DEFAULT_HEIGHT,
  loading = false,
  emptyMessage,
  showGrid = true,
  showLegend = true,
  showTooltip = true,
  animate = true,
  xAxisFormatter,
  yAxisFormatter,
  yAxisUnit = '',
  tooltipFormatter,
  tooltipLabelFormatter,
  layout = 'horizontal',
  stacked = false,
  barSize,
  barGap = 4,
}) => {
  const tokens = usePremiumTokens();
  const chartColors = useChartColors();

  if (loading) return <ChartSkeleton height={height} />;
  if (!data || data.length === 0) return <EmptyChart height={height} message={emptyMessage} />;

  const isVertical = layout === 'vertical';

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart
        data={data}
        layout={layout}
        margin={{ top: 10, right: 30, left: isVertical ? 80 : 0, bottom: 0 }}
        barGap={barGap}
      >
        {showGrid && (
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={chartColors.grid}
            horizontal={!isVertical}
            vertical={isVertical}
          />
        )}
        {isVertical ? (
          <>
            <XAxis
              type="number"
              stroke={chartColors.axis}
              tick={{ fill: chartColors.axisLabel, fontSize: 11 }}
              tickLine={{ stroke: chartColors.axis }}
              axisLine={{ stroke: chartColors.axis }}
              tickFormatter={yAxisFormatter}
              unit={yAxisUnit}
            />
            <YAxis
              type="category"
              dataKey={xKey}
              stroke={chartColors.axis}
              tick={{ fill: chartColors.axisLabel, fontSize: 11 }}
              tickLine={{ stroke: chartColors.axis }}
              axisLine={{ stroke: chartColors.axis }}
              tickFormatter={xAxisFormatter}
              width={70}
            />
          </>
        ) : (
          <>
            <XAxis
              dataKey={xKey}
              stroke={chartColors.axis}
              tick={{ fill: chartColors.axisLabel, fontSize: 11 }}
              tickLine={{ stroke: chartColors.axis }}
              axisLine={{ stroke: chartColors.axis }}
              tickFormatter={xAxisFormatter}
            />
            <YAxis
              stroke={chartColors.axis}
              tick={{ fill: chartColors.axisLabel, fontSize: 11 }}
              tickLine={{ stroke: chartColors.axis }}
              axisLine={{ stroke: chartColors.axis }}
              tickFormatter={yAxisFormatter}
              unit={yAxisUnit}
            />
          </>
        )}
        {showTooltip && (
          <Tooltip
            content={
              <ChartTooltip
                formatter={tooltipFormatter}
                labelFormatter={tooltipLabelFormatter}
              />
            }
          />
        )}
        {showLegend && <Legend content={<ChartLegend />} />}
        {bars.map((bar, index) => (
          <Bar
            key={bar.dataKey}
            dataKey={bar.dataKey}
            name={bar.name || bar.dataKey}
            fill={bar.color || chartColors.series[index % chartColors.series.length]}
            stackId={stacked ? 'stack' : undefined}
            barSize={barSize}
            radius={bar.radius || [4, 4, 0, 0]}
            isAnimationActive={animate}
            animationDuration={ANIMATION_DURATION}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

PremiumBarChart.propTypes = {
  data: PropTypes.array.isRequired,
  xKey: PropTypes.string.isRequired,
  bars: PropTypes.arrayOf(
    PropTypes.shape({
      dataKey: PropTypes.string.isRequired,
      name: PropTypes.string,
      color: PropTypes.string,
      radius: PropTypes.array,
    })
  ).isRequired,
  height: PropTypes.number,
  loading: PropTypes.bool,
  emptyMessage: PropTypes.string,
  showGrid: PropTypes.bool,
  showLegend: PropTypes.bool,
  showTooltip: PropTypes.bool,
  animate: PropTypes.bool,
  xAxisFormatter: PropTypes.func,
  yAxisFormatter: PropTypes.func,
  yAxisUnit: PropTypes.string,
  tooltipFormatter: PropTypes.func,
  tooltipLabelFormatter: PropTypes.func,
  layout: PropTypes.oneOf(['horizontal', 'vertical']),
  stacked: PropTypes.bool,
  barSize: PropTypes.number,
  barGap: PropTypes.number,
};

// ============================================================================
// COMPONENTE: PremiumPieChart
// ============================================================================

export const PremiumPieChart = ({
  data,
  dataKey,
  nameKey,
  colors,
  height = DEFAULT_HEIGHT,
  loading = false,
  emptyMessage,
  showLegend = true,
  showTooltip = true,
  showLabels = false,
  animate = true,
  innerRadius = 0,
  outerRadius,
  tooltipFormatter,
  labelFormatter,
}) => {
  const tokens = usePremiumTokens();
  const chartColors = useChartColors();
  const colorPalette = colors || chartColors.series;

  if (loading) return <ChartSkeleton height={height} />;
  if (!data || data.length === 0) return <EmptyChart height={height} message={emptyMessage} />;

  // Calculate radius based on height if not provided
  const calculatedOuterRadius = outerRadius || (height / 2) - 40;

  const renderLabel = ({ cx, cy, midAngle, innerRadius: ir, outerRadius: or, percent, name }) => {
    if (!showLabels || percent < 0.05) return null;

    const RADIAN = Math.PI / 180;
    const radius = ir + (or - ir) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill={tokens.text?.primary || '#f0f6fc'}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={11}
        fontWeight={600}
      >
        {labelFormatter ? labelFormatter(percent, name) : `${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        {showTooltip && (
          <Tooltip
            content={
              <ChartTooltip
                formatter={tooltipFormatter}
              />
            }
          />
        )}
        {showLegend && <Legend content={<ChartLegend />} />}
        <Pie
          data={data}
          dataKey={dataKey}
          nameKey={nameKey}
          cx="50%"
          cy="50%"
          innerRadius={innerRadius}
          outerRadius={calculatedOuterRadius}
          paddingAngle={innerRadius > 0 ? 2 : 0}
          label={showLabels ? renderLabel : false}
          labelLine={false}
          isAnimationActive={animate}
          animationDuration={ANIMATION_DURATION}
        >
          {data.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={entry.color || colorPalette[index % colorPalette.length]}
              stroke="transparent"
            />
          ))}
        </Pie>
      </PieChart>
    </ResponsiveContainer>
  );
};

PremiumPieChart.propTypes = {
  data: PropTypes.array.isRequired,
  dataKey: PropTypes.string.isRequired,
  nameKey: PropTypes.string.isRequired,
  colors: PropTypes.array,
  height: PropTypes.number,
  loading: PropTypes.bool,
  emptyMessage: PropTypes.string,
  showLegend: PropTypes.bool,
  showTooltip: PropTypes.bool,
  showLabels: PropTypes.bool,
  animate: PropTypes.bool,
  innerRadius: PropTypes.number,
  outerRadius: PropTypes.number,
  tooltipFormatter: PropTypes.func,
  labelFormatter: PropTypes.func,
};

// ============================================================================
// COMPONENTE: PremiumDonutChart (Alias)
// ============================================================================

export const PremiumDonutChart = (props) => (
  <PremiumPieChart {...props} innerRadius={props.innerRadius || 60} />
);

PremiumDonutChart.propTypes = {
  ...PremiumPieChart.propTypes,
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

export default PremiumLineChart;
